import { Box, NumberInput, Select, TextInput } from '@mantine/core';
import type { SDWDocumentCharacteristicsController } from '@dealstack/frontend/admin/pages/Manage/MIP/ManageMip/SimpleDocumentWorkflowBatch/controller/sdwdocumentCharacteristics';
import { DocumentSignatureType, DocumentTemplateType } from '@dealstack/shared/lib/generated';
import { AdvancedSwitch } from '@dealstack/frontend/shared/components/Switch/AdvancedSwitch.tsx';

interface Props {
	controller: ReturnType<typeof SDWDocumentCharacteristicsController>;
}

export const DocumentCharacteristics = ({ controller }: Props) => {
	const {
		templateType,
		setTemplateType,
		requiredWitnesses,
		setRequiredWitnesses,
		signatureType,
		setSignatureType,
		isSignatureAdvanced,
		setIsSignatureAdvanced,
		individualScheduleRequired,
		setIndividualScheduleRequired,
		isToBeSubmitted,
		setIsToBeSubmitted,
		emailText,
		setEmailText,
		guidanceNoteFileId,
		setGuidanceNoteFileId,
		counterparties,
		addCounterparty,
		setCounterparties,
		removeLastCounterparty,
	} = controller;

	return (
		<Box>
			<Select
				label="Template Type"
				value={templateType}
				onChange={(value: DocumentTemplateType) => setTemplateType(value)}
				data={Object.values(DocumentTemplateType).map((type) => ({
					value: type,
					label: type,
				}))}
			/>

			<NumberInput
				label="Required Witnesses"
				value={requiredWitnesses}
				onChange={(value) => setRequiredWitnesses(Number(value) || 0)}
				min={0}
				max={10}
			/>

			<Select
				label="Signature Type"
				value={signatureType}
				onChange={(value: DocumentSignatureType) => setSignatureType(value)}
				disabled={isToBeSubmitted}
				description={
					isToBeSubmitted
						? 'Locked to Electronic while Submit and Sign is enabled (submit-then-sign requires e-signing).'
						: undefined
				}
				data={Object.values(DocumentSignatureType).map((type) => ({
					value: type,
					label: type,
				}))}
			/>

			<AdvancedSwitch
				rightText="Advanced Signature"
				selectedValue={isSignatureAdvanced}
				onChange={(event) => setIsSignatureAdvanced(!!event)}
				selectedColor="dark.8"
				unselectedColor="dark.8"
			/>

			<AdvancedSwitch
				rightText="Individual Schedule Required"
				selectedValue={individualScheduleRequired}
				onChange={(event) => setIndividualScheduleRequired(!!event)}
				selectedColor="dark.8"
				unselectedColor="dark.8"
			/>

			<AdvancedSwitch
				rightText="Submit and Sign (investor submits the completed form, then e-signs — requires Electronic signature type)"
				selectedValue={isToBeSubmitted}
				onChange={(event) => setIsToBeSubmitted(!!event)}
				selectedColor="dark.8"
				unselectedColor="dark.8"
			/>

			<TextInput
				label="Email Text"
				value={emailText || ''}
				onChange={(event) => setEmailText(event.currentTarget.value)}
				placeholder="Enter email text..."
			/>

			<TextInput
				label="Guidance Note File ID"
				value={guidanceNoteFileId || ''}
				onChange={(event) => setGuidanceNoteFileId(event.currentTarget.value)}
				placeholder="Enter guidance note file ID..."
			/>

			{/* Counterparty Management */}
			<Box mt="md">
				<Box sx={{ display: 'flex', gap: '1rem', alignItems: 'flex-end' }}>
					<TextInput
						label="Counterparty Name"
						placeholder="Enter counterparty name"
						style={{ flex: 1 }}
					/>
					<NumberInput
						label="Amount"
						placeholder="Enter amount"
						min={1}
						max={6}
						style={{ width: '120px' }}
					/>
					<button
						onClick={(e) => {
							const nameInput = e.currentTarget.parentElement?.querySelector(
								'input[placeholder="Enter counterparty name"]'
							) as HTMLInputElement;
							const amountInput = e.currentTarget.parentElement?.querySelector(
								'input[placeholder="Enter amount"]'
							) as HTMLInputElement;

							if (nameInput.value && amountInput.value) {
								addCounterparty(nameInput.value, Number(amountInput.value));
								nameInput.value = '';
								amountInput.value = '';
							}
						}}
						style={{
							padding: '8px 16px',
							border: '1px solid #dee2e6',
							borderRadius: '4px',
							background: 'white',
							cursor: 'pointer',
							height: '36px',
						}}
					>
						Add
					</button>
				</Box>

				{counterparties.map((counterparty, index) => (
					<Box
						key={index}
						mt="xs"
						sx={{
							display: 'flex',
							alignItems: 'center',
							gap: '1rem',
							padding: '0.5rem',
							borderRadius: '4px',
							backgroundColor: '#f8f9fa',
						}}
					>
						<div style={{ flex: 1 }}>
							{counterparty.name} - {counterparty.amount}
						</div>
						<button
							onClick={() =>
								setCounterparties((prev) => prev.filter((_, i) => i !== index))
							}
							style={{
								border: 'none',
								background: 'none',
								cursor: 'pointer',
								color: 'red',
								padding: '4px 8px',
							}}
						>
							✕
						</button>
					</Box>
				))}

				{counterparties.length > 0 && (
					<button
						onClick={removeLastCounterparty}
						style={{
							marginTop: '0.5rem',
							padding: '4px 8px',
							border: '1px solid #dee2e6',
							borderRadius: '4px',
							background: 'white',
							cursor: 'pointer',
						}}
					>
						Remove Last Counterparty
					</button>
				)}
			</Box>
		</Box>
	);
};
